import React, { useState, useMemo, useRef, useEffect } from "react";
import { X, Filter, Search, Bell, ChevronDown, ChevronLeft, ChevronRight, User, RotateCcw, Users, Building, Layers } from "lucide-react";
import MainLayout from "./MainLayout";
import "../styles/organizationLeave.css";
import { useAuth } from "../context/AuthContext";

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL || "http://localhost:3030"}/api`;

// Reads JWT token from wherever your app stores it after login.
// Adjust the key name to match what your auth flow saves —
// e.g. "token", "access_token", "authToken", etc.
function getAuthHeaders() {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("token") ||
    "";
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// Normalize any date string / ISO timestamp → "YYYY-MM-DD"
function toYMD(d) {
  if (!d) return "";
  if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  const dt = new Date(d);
  if (isNaN(dt)) return "";
  return dt.toISOString().slice(0, 10);
}

function fmtDate(d) {
  if (!d) return "--";
  const ymd = toYMD(d);
  if (!ymd) return "--";
  const date = new Date(ymd + "T00:00:00");
  const local = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  return `${String(local.getDate()).padStart(2, '0')}-${String(local.getMonth() + 1).padStart(2, '0')}-${local.getFullYear()}`;
}

function Highlight({ text, query }) {
  if (!query || !text) return <>{text}</>;
  const str = String(text);
  const idx = str.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{str}</>;
  return (
    <>
      {str.slice(0, idx)}
      <mark className="tr-highlight">{str.slice(idx, idx + query.length)}</mark>
      {str.slice(idx + query.length)}
    </>
  );
}

function SummaryCard({ label, count, variant }) {
  const styles = {
    pending: { card: "tr-sum--pending", num: "#D97706" },
    approved: { card: "tr-sum--approved", num: "#059669" },
    rejected: { card: "tr-sum--rejected", num: "#DC2626" },
  };
  return (
    <div className={`tr-summary-card ${styles[variant].card}`}>
      <p className="tr-sum-label">{label}</p>
      <p className="tr-sum-count" style={{ color: styles[variant].num }}>{count}</p>
    </div>
  );
}

function avatarInitials(name) {
  if (!name) return "?";
  return name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
}

const AVATAR_COLORS = ["#6C8EF5", "#F59E0B", "#10B981", "#8B5CF6", "#EC4899", "#3B82F6", "#EF4444", "#14B8A6"];
function avatarColor(str) {
  if (!str) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export default function OrganizationLeaveHistory() {
  const { user, token: authToken, logout } = useAuth();
  const [userRole] = useState("ADMIN");

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(p => !p);

  // ── Data state ──────────────────────────────────────
  const [requests, setRequests] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [departmentMap, setDepartmentMap] = useState({}); // code → name
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Filters ─────────────────────────────────────────
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterLeaveType, setFilterLeaveType] = useState("all");
  const [filterDepartment, setFilterDepartment] = useState("all");
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");
  const [empSearch, setEmpSearch] = useState("");
  const [showEmpDropdown, setShowEmpDropdown] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const [showFilterSidebar, setShowFilterSidebar] = useState(false);
  const [showGroupByDropdown, setShowGroupByDropdown] = useState(false);
  const [groupBy, setGroupBy] = useState("None"); // Department, Employee, Leave Type, Status, None

  const empDropRef = useRef(null);

  // ── Fetch all data on mount ──────────────────────────
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        const headers = getAuthHeaders();
        const [leavesRes, exceptionRes, statusRes, leaveTypesRes, deptsRes] = await Promise.all([
          fetch(`${BASE_URL}/leave-requests/`, { headers }),
          fetch(`${BASE_URL}/employee-requests/`, { headers }).catch(() => ({ ok: false })),
          fetch(`${BASE_URL}/dropdowns/values/STATUS`, { headers }),
          fetch(`${BASE_URL}/leave-types`, { headers }),
          fetch(`${BASE_URL}/department-master`, { headers }),
        ]);

        if (!leavesRes.ok) throw new Error(`Leave requests fetch failed: ${leavesRes.status}`);
        if (!statusRes.ok) throw new Error(`Status dropdown fetch failed: ${statusRes.status}`);
        if (!leaveTypesRes.ok) throw new Error(`Leave types fetch failed: ${leaveTypesRes.status}`);

        const [leavesData, exceptionData, statusData, leaveTypesData, deptsData] = await Promise.all([
          leavesRes.json(),
          exceptionRes.ok ? exceptionRes.json() : Promise.resolve([]),
          statusRes.json(),
          leaveTypesRes.json(),
          deptsRes.ok ? deptsRes.json() : Promise.resolve([]),
        ]);

        // leave-requests & employee-requests
        const leavesArr = Array.isArray(leavesData) ? leavesData : (leavesData.data || []);
        const exceptionArr = Array.isArray(exceptionData.data) ? exceptionData.data : (Array.isArray(exceptionData) ? exceptionData : []);
        const formattedExceptions = exceptionArr.map(row => ({
          ...row,
          isException: true,
          leave_type_name: row.request_type, // Map it correctly so the filtering/rendering uses this
        }));

        setRequests([...leavesArr, ...formattedExceptions].sort((a, b) => new Date(b.created_at || b.start_date) - new Date(a.created_at || a.start_date)));

        // Build department code → name map
        // API returns { success: true, data: [{ department_id, department_name }] }
        const rawDepts = Array.isArray(deptsData)
          ? deptsData
          : (deptsData.data || deptsData.items || []);
        const deptMap = {};
        rawDepts.forEach(d => {
          const code = d.department_id || d.id || "";
          const name = d.department_name || d.name || code;
          if (code) deptMap[code] = name;
        });
        setDepartmentMap(deptMap);

        // STATUS: normalise every entry to plain { val, label } strings
        const rawStatus = Array.isArray(statusData)
          ? statusData
          : (statusData.values || statusData.data || statusData.items || []);
        const normStatus = rawStatus.map(opt => {
          if (typeof opt === "string") return { val: opt, label: opt };
          const val = String(opt.dropdown_value_code ?? opt.value ?? opt.id ?? "");
          const label = String(opt.dropdown_value_name ?? opt.label ?? opt.name ?? val);
          return { val, label };
        });
        setStatusOptions(normStatus);

        // leave-types
        const rawTypes = Array.isArray(leaveTypesData)
          ? leaveTypesData
          : (leaveTypesData.data || leaveTypesData.items || []);
        setLeaveTypes(rawTypes);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Click-outside for employee dropdown
  useEffect(() => {
    const handler = (e) => {
      if (empDropRef.current && !empDropRef.current.contains(e.target))
        setShowEmpDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Derived counts ───────────────────────────────────
  const pendingCount = requests.filter(r => (r.status || "").toUpperCase() === "PENDING").length;
  const approvedCount = requests.filter(r => (r.status || "").toUpperCase() === "APPROVED").length;
  const rejectedCount = requests.filter(r => (r.status || "").toUpperCase() === "REJECTED").length;

  // ── Departments list for filter dropdown ──
  // Use department codes from leave requests; display resolved names via departmentMap
  const departments = useMemo(() => {
    const set = new Set();
    requests.forEach(r => { if (r.department) set.add(r.department); });
    return [...set].sort();
  }, [requests]);

  // Helper: resolve department code → name
  const deptName = (code) => departmentMap[code] || code || "--";

  // ── Employee name suggestions ────────────────────────
  const nameSuggestions = useMemo(() => {
    if (!empSearch.trim()) return [];
    const q = empSearch.toLowerCase();
    const seen = new Set();
    return requests
      .filter(r => {
        const name = r.employee_name || r.employeeName || "";
        const id = r.employee_id || r.employeeId || "";
        return (
          (name.toLowerCase().includes(q) || id.toLowerCase().includes(q)) &&
          !seen.has(name) && seen.add(name)
        );
      })
      .map(r => ({
        name: r.employee_name || r.employeeName || "",
        id: r.employee_id || r.employeeId || "",
      }))
      .slice(0, 5);
  }, [empSearch, requests]);

  // ── Active filter count ──────────────────────────────
  const activeFilterCount = [
    filterStatus !== "all",
    filterLeaveType !== "all",
    filterDepartment !== "all",
    !!filterFromDate,
    !!filterToDate,
    !!empSearch.trim(),
  ].filter(Boolean).length;

  const resetFilters = () => {
    setFilterStatus("all"); setFilterLeaveType("all"); setFilterDepartment("all");
    setFilterFromDate(""); setFilterToDate(""); setEmpSearch(""); setPage(1);
  };

  // ── Filtered results ─────────────────────────────────
  const filtered = useMemo(() => {
    return requests.filter(r => {
      const status = (r.status || "").toLowerCase();
      const leaveType = r.leave_type_name || r.leaveType || "";
      const dept = r.department || "";
      const name = (r.employee_name || r.employeeName || "").toLowerCase();
      const empId = (r.employee_id || r.employeeId || "").toLowerCase();

      const startDate = toYMD(r.start_date || r.startDate || "");
      const endDate = toYMD(r.end_date || r.endDate || "");

      if (filterStatus !== "all" && status !== filterStatus.toLowerCase()) return false; // filterStatus is dropdown_value_code e.g. "PENDING" 
      if (filterLeaveType !== "all" && leaveType !== filterLeaveType) return false;
      if (filterDepartment !== "all" && dept !== filterDepartment) return false;

      // Compare normalized YYYY-MM-DD strings safely
      if (filterFromDate && startDate && startDate < filterFromDate) return false;
      if (filterToDate && startDate && startDate > filterToDate) return false;

      if (empSearch.trim()) {
        const q = empSearch.toLowerCase();
        if (!name.includes(q) && !empId.includes(q)) return false;
      }
      return true;
    });
  }, [requests, filterStatus, filterLeaveType, filterDepartment, filterFromDate, filterToDate, empSearch]);

  const sortedAndGrouped = useMemo(() => {
    if (groupBy === "None") return filtered;
    return [...filtered].sort((a, b) => {
      let keyA = "", keyB = "";
      if (groupBy === "Department") {
        const deptA = a.department || "";
        const deptB = b.department || "";
        keyA = (departmentMap[deptA] || deptA || "--").toLowerCase();
        keyB = (departmentMap[deptB] || deptB || "--").toLowerCase();
      } else if (groupBy === "Employee") {
        keyA = (a.employee_name || a.employeeName || a.employee_id || a.employeeId || "").toLowerCase();
        keyB = (b.employee_name || b.employeeName || b.employee_id || b.employeeId || "").toLowerCase();
      } else if (groupBy === "Leave Type") {
        keyA = (a.leave_type_name || a.leaveType || "").toLowerCase();
        keyB = (b.leave_type_name || b.leaveType || "").toLowerCase();
      } else if (groupBy === "Status") {
        keyA = (a.status || "").toLowerCase();
        keyB = (b.status || "").toLowerCase();
      }
      return keyA.localeCompare(keyB);
    });
  }, [filtered, groupBy, departmentMap]);

  const paginated = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return sortedAndGrouped.slice(start, start + rowsPerPage);
  }, [sortedAndGrouped, page, rowsPerPage]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [filterStatus, filterLeaveType, filterDepartment, filterFromDate, filterToDate, empSearch, groupBy]);

  // ── Status badge helper ──────────────────────────────
  const statusMeta = (status) => {
    const s = (status || "").toUpperCase();
    if (s === "PENDING") return { label: "Pending", cls: "tr-badge--pending" };
    if (s === "APPROVED") return { label: "Approved", cls: "tr-badge--approved" };
    if (s === "REJECTED") return { label: "Rejected", cls: "tr-badge--rejected" };
    return { label: status, cls: "" };
  };

  return (
      <MainLayout sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} title="Organization Leave History">
        <div style={{ flex: 1 }}>

          {!["ADMIN", "HR"].includes(userRole) ? (
            <div style={{ padding: "60px 20px", textAlign: "center", color: "#718096" }}>
              <Users size={64} />
              <h2 style={{ fontSize: "24px", margin: "16px 0 12px", color: "#1A202C" }}>Access Restricted</h2>
              <p style={{ fontSize: "15px", maxWidth: "400px", margin: "0 auto" }}>Only Admin and HR roles can view Organization Leave History</p>
            </div>
          ) : (
            <div className="tr-wrap" style={{ padding: 0 }}>
              {/* Top Header removed and handled by MainLayout */}

              {/* Summary Cards */}
              <div className="tr-summary-grid">
                <SummaryCard label="PENDING" count={pendingCount} variant="pending" />
                <SummaryCard label="APPROVED" count={approvedCount} variant="approved" />
                <SummaryCard label="REJECTED" count={rejectedCount} variant="rejected" />
              </div>

              {/* Action Bar */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 14px', flex: '1', minWidth: '200px', maxWidth: '420px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                  <Search size={16} color="#94a3b8" style={{ flexShrink: 0 }} />
                  <input 
                    style={{ border: 'none', outline: 'none', padding: '4px 10px', width: '100%', fontSize: '14px', color: '#1e293b', background: 'transparent' }} 
                    placeholder="Search by name or ID..." 
                    value={empSearch} 
                    onChange={e => setEmpSearch(e.target.value)} 
                  />
                  {empSearch && (
                    <button onClick={() => setEmpSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, display: 'flex' }}>
                      <X size={14} />
                    </button>
                  )}
                </div>
                
                <div style={{ display: 'flex', gap: '10px' }}>
                  {/* Filter Button */}
                  <div style={{ position: 'relative' }}>
                    <button onClick={() => setShowFilterSidebar(true)} style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 14px', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#334155' }}>
                      <Filter size={14} color="#64748b" /> Filter {activeFilterCount > 0 && <span style={{ background: '#3b82f6', color: '#fff', borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}>{activeFilterCount}</span>}
                    </button>
                  </div>
                  
                  {/* Group By Button */}
                  <div style={{ position: 'relative' }}>
                    <button onClick={() => setShowGroupByDropdown(!showGroupByDropdown)} style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 14px', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#334155' }}>
                      <Layers size={14} color="#64748b" /> Group by
                    </button>
                    {showGroupByDropdown && (
                      <div style={{ position: 'absolute', top: '100%', right: '0', marginTop: '8px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 0', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, minWidth: '160px' }}>
                        {["Department", "Employee", "Leave Type", "Status", "None"].map(opt => (
                          <div 
                            key={opt} 
                            onClick={() => { setGroupBy(opt); setShowGroupByDropdown(false); }}
                            style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', background: groupBy === opt ? '#f1f5f9' : 'transparent', color: groupBy === opt ? '#0f172a' : '#475569' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                            onMouseLeave={e => e.currentTarget.style.background = groupBy === opt ? '#f1f5f9' : 'transparent'}
                          >
                            {opt}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Filter Sidebar */}
              {showFilterSidebar && (
                <>
                  <div className="tr-modal-backdrop" onClick={() => setShowFilterSidebar(false)} style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.4)', zIndex: 999 }} />
                  <div style={{ position: 'fixed', top: 0, right: 0, width: '340px', height: '100vh', background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.1)', zIndex: 1000, display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.3s ease' }}>
                    <style>{`
                      @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
                    `}</style>
                    <div style={{ padding: '24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#0f172a' }}>Filters</h3>
                      <button onClick={() => setShowFilterSidebar(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><X size={20} /></button>
                    </div>
                    
                    <div style={{ padding: '24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {/* Department */}
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Department</label>
                        <select value={filterDepartment} onChange={e => setFilterDepartment(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', backgroundColor: '#f8fafc' }}>
                          <option value="all">All Departments</option>
                          {departments.map(dept => <option key={dept} value={dept}>{deptName(dept)}</option>)}
                        </select>
                      </div>

                      {/* Status */}
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Status</label>
                        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', backgroundColor: '#f8fafc' }}>
                          <option value="all">All Status</option>
                          {statusOptions.map((opt, i) => (
                            <option key={opt.val + i} value={opt.val}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Leave Type */}
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Leave Type</label>
                        <select value={filterLeaveType} onChange={e => setFilterLeaveType(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', backgroundColor: '#f8fafc' }}>
                          <option value="all">All Types</option>
                          {leaveTypes.map(lt => {
                            const val = lt.name || lt.id;
                            const label = lt.name || lt.label;
                            return <option key={lt.id || val} value={val}>{label}</option>;
                          })}
                        </select>
                      </div>

                      {/* Date Range */}
                      <div>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Date Range</label>
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>From</label>
                            <input type="date" value={filterFromDate} max={filterToDate || undefined} onChange={e => setFilterFromDate(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px', backgroundColor: '#f8fafc' }} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>To</label>
                            <input type="date" value={filterToDate} min={filterFromDate || undefined} onChange={e => setFilterToDate(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px', backgroundColor: '#f8fafc' }} />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ padding: '20px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: '12px' }}>
                      <button onClick={resetFilters} style={{ flex: 1, padding: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#fff', border: '1px solid #fee2e2', borderRadius: '8px', gap: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#dc2626' }}>
                        <RotateCcw size={14} /> Reset
                      </button>
                      <button onClick={() => setShowFilterSidebar(false)} style={{ flex: 1, padding: '10px', background: '#3b82f6', border: 'none', borderRadius: '8px', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                        Apply
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Table */}
              <div className="tr-table-card">
                {loading ? (
                  <div className="tr-empty"><p>Loading leave requests...</p></div>
                ) : error ? (
                  <div className="tr-empty" style={{ color: "#DC2626" }}>
                    <p>Error: {error}</p>
                  </div>
                ) : (
                  <div className="tr-table-scroll">
                    {filtered.length === 0 ? (
                      <div className="tr-empty"><p>No matching requests</p></div>
                    ) : (
                      <>
                        <table className="tr-table">
                          <thead>
                            <tr>
                              {["EMPLOYEE", "EMP ID", "DEPARTMENT", "LEAVE TYPE", "DATE RANGE", "UNITS", "STATUS"].map(h => (
                                <th key={h} className="tr-th">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {paginated.map((req, i) => {
                              let currentGroup = null;
                              if (groupBy !== "None") {
                                if (groupBy === "Department") {
                                  const deptCode = req.department || "";
                                  currentGroup = deptCode ? deptName(deptCode) : "--";
                                } else if (groupBy === "Employee") {
                                  currentGroup = req.employee_name || req.employeeName || req.employee_id || req.employeeId || "--";
                                } else if (groupBy === "Leave Type") {
                                  currentGroup = req.leave_type_name || req.leaveType || "--";
                                } else if (groupBy === "Status") {
                                  currentGroup = (req.status || "").toUpperCase();
                                }
                              }
                              
                              let prevGroup = null;
                              if (i > 0 && groupBy !== "None") {
                                const prevReq = paginated[i - 1];
                                if (groupBy === "Department") {
                                  const pDeptCode = prevReq.department || "";
                                  prevGroup = pDeptCode ? deptName(pDeptCode) : "--";
                                } else if (groupBy === "Employee") {
                                  prevGroup = prevReq.employee_name || prevReq.employeeName || prevReq.employee_id || prevReq.employeeId || "--";
                                } else if (groupBy === "Leave Type") {
                                  prevGroup = prevReq.leave_type_name || prevReq.leaveType || "--";
                                } else if (groupBy === "Status") {
                                  prevGroup = (prevReq.status || "").toUpperCase();
                                }
                              }
                              
                              const showGroupHeader = groupBy !== "None" && currentGroup !== prevGroup;

                              const name = req.employee_name || req.employeeName || "Unknown";
                              const empId = req.employee_id || req.employeeId || "--";
                              const deptCode = req.department || "";
                              const dept = deptCode ? deptName(deptCode) : "--";
                              const ltName = req.leave_type_name || req.leaveType || "--";
                              const startDate = req.start_date || req.startDate || "";
                              const endDate = req.end_date || req.endDate || "";
                              const units = req.total_units || req.units || 0;
                              const unitType = req.unit_type || req.unitType || "DAY";
                              const isLop = req.is_lop || req.isLop || false;
                              const meta = statusMeta(req.status);

                              return (
                                <React.Fragment key={req.id}>
                                  {showGroupHeader && (
                                    <tr>
                                      <td colSpan="7" style={{ background: '#f8fafc', fontWeight: 'bold', borderBottom: '1px solid #e2e8f0', padding: '12px 16px', color: '#334155' }}>
                                        {currentGroup}
                                      </td>
                                    </tr>
                                  )}
                                  <tr className="tr-row" style={{ animationDelay: `${i * 40}ms` }}>
                                    <td className="tr-td">
                                      <div className="tr-emp">
                                        <span className="tr-avatar" style={{ background: avatarColor(name) }}>
                                          {avatarInitials(name)}
                                        </span>
                                        <div className="tr-emp-name-wrapper" style={{ display: 'flex', flexDirection: 'column' }}>
                                          <span className="tr-emp-name">
                                            <Highlight text={name} query={empSearch} />
                                          </span>
                                          {req.isException && (
                                            <span style={{ fontSize: '10px', background: '#F97316', color: '#FFF', padding: '2px 6px', borderRadius: '4px', marginTop: '2px', width: 'fit-content', fontWeight: 'bold' }}>
                                              EXCEPTION
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                    <td className="tr-td">
                                      <span className="tr-empid-badge">
                                        <Highlight text={empId} query={empSearch} />
                                      </span>
                                    </td>
                                    <td className="tr-td tr-td--muted"><strong>{dept}</strong></td>
                                    <td className="tr-td tr-td--muted">{ltName}</td>
                                    <td className="tr-td tr-td--muted">
                                      {fmtDate(startDate)} – {fmtDate(endDate)}
                                    </td>
                                    <td className="tr-td tr-td--muted">
                                      {units} {unitType === "HOUR" ? "hrs" : Number(units) === 1 ? "day" : "days"}
                                    </td>
                                    <td className="tr-td">
                                      <span className={`tr-status-badge ${meta.cls}`}>{meta.label}</span>
                                    </td>
                                  </tr>
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>

                        {/* Pagination moved outside */}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Pagination */}
              {!loading && !error && filtered.length > 0 && (
                <div className="em-pagination">
                  <div className="em-pagination__left">
                    <span className="em-pagination__info">
                      Showing {Math.min((page - 1) * rowsPerPage + 1, filtered.length)}–{Math.min(page * rowsPerPage, filtered.length)} of {filtered.length}
                    </span>
                    <div className="em-rows-selector">
                      <span className="em-rows-label">Rows:</span>
                      <select
                        className="em-rows-select"
                        value={rowsPerPage}
                        onChange={(e) => {
                          setRowsPerPage(Number(e.target.value));
                          setPage(1);
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={30}>30</option>
                        <option value={40}>40</option>
                      </select>
                    </div>
                  </div>
                  <div className="em-pagination__btns">
                    <button className="em-page-btn" disabled={page === 1} onClick={() => setPage(page - 1)}><ChevronLeft size={15} /></button>
                    {Array.from({ length: Math.ceil(filtered.length / rowsPerPage) }, (_, i) => (
                      <button key={i + 1} className={`em-page-btn${page === i + 1 ? " active" : ""}`} onClick={() => setPage(i + 1)}>{i + 1}</button>
                    ))}
                    <button className="em-page-btn" disabled={page === Math.ceil(filtered.length / rowsPerPage)} onClick={() => setPage(page + 1)}><ChevronRight size={15} /></button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </MainLayout>
  );
}